import React, { useEffect, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import { Card, CardHeader, CardBody, Row, Col, Button, Table, FormGroup, Label, Input } from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

function rowResults(r) {
  const actions = Array.isArray(r.actions) ? r.actions : [];
  const actionPriority = ["purchase", "offsite_conversion.purchase", "lead", "add_to_cart", "link_click"];
  for (const type of actionPriority) {
    const hit = actions.find((a) => a.action_type === type);
    if (hit) return num(hit.value);
  }
  return actions.length ? num(actions[0].value) : 0;
}

const MetaCampaignDetail = () => {
  const { adAccountId, campaignId } = useParams();
  const history = useHistory();
  const { token: authToken, userLanguage } = useSelector((state) => state.auth);
  const t = {
    title: { en: "Meta Campaign Detail", fr: "Detail de campagne Meta", es: "Detalle de campana Meta", zh: "Meta广告活动详情", pt: "Detalhe da campanha Meta", ar: "تفاصيل حملة ميتا" },
    back: { en: "Back", fr: "Retour", es: "Atras", zh: "返回", pt: "Voltar", ar: "رجوع" },
    notFound: { en: "Campaign not found.", fr: "Campagne introuvable.", es: "Campana no encontrada.", zh: "未找到广告活动。", pt: "Campanha nao encontrada.", ar: "الحملة غير موجودة." },
    name: { en: "Name", fr: "Nom", es: "Nombre", zh: "名称", pt: "Nome", ar: "الاسم" },
    campaignId: { en: "Campaign ID", fr: "ID de campagne", es: "ID de campana", zh: "广告活动ID", pt: "ID da campanha", ar: "معرف الحملة" },
    status: { en: "Status", fr: "Statut", es: "Estado", zh: "状态", pt: "Status", ar: "الحالة" },
    objective: { en: "Objective", fr: "Objectif", es: "Objetivo", zh: "目标", pt: "Objetivo", ar: "الهدف" },
    created: { en: "Created", fr: "Cree", es: "Creado", zh: "创建时间", pt: "Criado", ar: "تاريخ الإنشاء" },
    period: { en: "Period", fr: "Periode", es: "Periodo", zh: "周期", pt: "Periodo", ar: "الفترة" },
    l7: { en: "Last 7 days", fr: "7 derniers jours", es: "Ultimos 7 dias", zh: "最近7天", pt: "Ultimos 7 dias", ar: "آخر 7 أيام" },
    l14: { en: "Last 14 days", fr: "14 derniers jours", es: "Ultimos 14 dias", zh: "最近14天", pt: "Ultimos 14 dias", ar: "آخر 14 يومًا" },
    l30: { en: "Last 30 days", fr: "30 derniers jours", es: "Ultimos 30 dias", zh: "最近30天", pt: "Ultimos 30 dias", ar: "آخر 30 يومًا" },
    refreshing: { en: "Refreshing...", fr: "Actualisation...", es: "Actualizando...", zh: "刷新中...", pt: "Atualizando...", ar: "جارٍ التحديث..." },
    refreshInsights: { en: "Refresh insights", fr: "Actualiser les statistiques", es: "Actualizar estadisticas", zh: "刷新数据", pt: "Atualizar insights", ar: "تحديث الإحصاءات" },
    impressions: { en: "Impressions", fr: "Impressions", es: "Impresiones", zh: "展示", pt: "Impressoes", ar: "مرات الظهور" },
    clicks: { en: "Clicks", fr: "Clics", es: "Clics", zh: "点击", pt: "Cliques", ar: "النقرات" },
    ctr: { en: "CTR", fr: "CTR", es: "CTR", zh: "点击率", pt: "CTR", ar: "معدل النقر" },
    spend: { en: "Spend", fr: "Depenses", es: "Gasto", zh: "花费", pt: "Gasto", ar: "الإنفاق" },
    cpc: { en: "CPC", fr: "CPC", es: "CPC", zh: "每次点击费用", pt: "CPC", ar: "تكلفة النقرة" },
    results: { en: "Results", fr: "Resultats", es: "Resultados", zh: "结果", pt: "Resultados", ar: "النتائج" },
    dailyInsights: { en: "Daily insights", fr: "Statistiques quotidiennes", es: "Estadisticas diarias", zh: "每日数据", pt: "Insights diarios", ar: "إحصاءات يومية" },
    date: { en: "Date", fr: "Date", es: "Fecha", zh: "日期", pt: "Data", ar: "التاريخ" },
    reach: { en: "Reach", fr: "Portee", es: "Alcance", zh: "覆盖人数", pt: "Alcance", ar: "الوصول" },
    noInsights: { en: "No insights data.", fr: "Aucune donnee statistique.", es: "Sin datos estadisticos.", zh: "暂无分析数据。", pt: "Sem dados de insights.", ar: "لا توجد بيانات إحصائية." },
    failDetail: { en: "Failed to load campaign detail", fr: "Echec du chargement du detail", es: "Error al cargar detalle", zh: "加载详情失败", pt: "Falha ao carregar detalhes", ar: "فشل تحميل التفاصيل" },
    failInsights: { en: "Failed to load campaign insights", fr: "Echec du chargement des statistiques", es: "Error al cargar estadisticas", zh: "加载数据失败", pt: "Falha ao carregar insights", ar: "فشل تحميل الإحصاءات" },
  };
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [datePreset, setDatePreset] = useState("last_7d");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    if (!authToken || !adAccountId || !campaignId) return;
    setLoading(true);
    axios
      .get(`/ad-accounts/${adAccountId}/campaigns?campaign_id=${encodeURIComponent(campaignId)}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const row = (res.data.data || [])[0] || null;
        setCampaign(row);
      })
      .catch((e) => toast.error(e.response?.data?.message || t.failDetail[userLanguage] || t.failDetail.en))
      .finally(() => setLoading(false));
  }, [authToken, adAccountId, campaignId]);

  const loadInsights = () => {
    if (!authToken || !adAccountId || !campaignId) return;
    setInsightsLoading(true);
    axios
      .get(
        `/meta/insights/campaign/${encodeURIComponent(campaignId)}?adAccountId=${encodeURIComponent(
          adAccountId
        )}&date_preset=${encodeURIComponent(datePreset)}&time_increment=1`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => setRows(res.data.data || []))
      .catch((e) => toast.error(e.response?.data?.message || t.failInsights[userLanguage] || t.failInsights.en))
      .finally(() => setInsightsLoading(false));
  };

  useEffect(() => {
    loadInsights();
  }, [authToken, adAccountId, campaignId, datePreset]);

  const totals = rows.reduce(
    (t, r) => {
      t.impressions += num(r.impressions);
      t.reach += num(r.reach);
      t.clicks += num(r.clicks);
      t.spend += num(r.spend);
      t.results += rowResults(r);
      return t;
    },
    { impressions: 0, reach: 0, clicks: 0, spend: 0, results: 0 }
  );
  const ctr = totals.impressions ? (totals.clicks / totals.impressions) * 100 : 0;
  const cpc = totals.clicks ? totals.spend / totals.clicks : 0;

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <b>{t.title[userLanguage] || t.title.en}</b>
              <Button color="secondary" size="sm" onClick={() => history.push("/seller/social-advertisement")}>
                {t.back[userLanguage] || t.back.en}
              </Button>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div className="text-center py-4">
                  <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                </div>
              ) : !campaign ? (
                <p className="text-muted">{t.notFound[userLanguage] || t.notFound.en}</p>
              ) : (
                <>
                  <Table bordered striped responsive size="sm">
                    <tbody>
                      <tr><th width="220">{t.name[userLanguage] || t.name.en}</th><td>{campaign.name || "-"}</td></tr>
                      <tr><th>{t.campaignId[userLanguage] || t.campaignId.en}</th><td style={{ fontFamily: "monospace" }}>{campaign.id || "-"}</td></tr>
                      <tr><th>{t.status[userLanguage] || t.status.en}</th><td>{campaign.status || "-"}</td></tr>
                      <tr><th>{t.objective[userLanguage] || t.objective.en}</th><td>{campaign.objective || "-"}</td></tr>
                      <tr><th>{t.created[userLanguage] || t.created.en}</th><td>{campaign.created_time ? new Date(campaign.created_time).toLocaleString() : "-"}</td></tr>
                    </tbody>
                  </Table>

                  <Row form className="align-items-end mb-3">
                    <Col md={6}>
                      <FormGroup>
                        <Label>{t.period[userLanguage] || t.period.en}</Label>
                        <Input type="select" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                          <option value="last_7d">{t.l7[userLanguage] || t.l7.en}</option>
                          <option value="last_14d">{t.l14[userLanguage] || t.l14.en}</option>
                          <option value="last_30d">{t.l30[userLanguage] || t.l30.en}</option>
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={6} className="text-right">
                      <Button color="secondary" size="sm" onClick={loadInsights} disabled={insightsLoading}>
                        {insightsLoading ? t.refreshing[userLanguage] || t.refreshing.en : t.refreshInsights[userLanguage] || t.refreshInsights.en}
                      </Button>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={2}>
                      <Card body>
                        <b>{t.impressions[userLanguage] || t.impressions.en}</b>
                        <div>{totals.impressions.toLocaleString()}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>{t.clicks[userLanguage] || t.clicks.en}</b>
                        <div>{totals.clicks.toLocaleString()}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>{t.ctr[userLanguage] || t.ctr.en}</b>
                        <div>{ctr.toFixed(2)}%</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>{t.spend[userLanguage] || t.spend.en}</b>
                        <div>${totals.spend.toFixed(2)}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>{t.cpc[userLanguage] || t.cpc.en}</b>
                        <div>${cpc.toFixed(2)}</div>
                      </Card>
                    </Col>
                    <Col md={2}>
                      <Card body>
                        <b>{t.results[userLanguage] || t.results.en}</b>
                        <div>{totals.results.toLocaleString()}</div>
                      </Card>
                    </Col>
                  </Row>

                  <Card>
                    <CardHeader><b>{t.dailyInsights[userLanguage] || t.dailyInsights.en}</b></CardHeader>
                    <CardBody>
                      <Table bordered striped responsive size="sm">
                        <thead>
                          <tr>
                            <th>{t.date[userLanguage] || t.date.en}</th>
                            <th>{t.impressions[userLanguage] || t.impressions.en}</th>
                            <th>{t.reach[userLanguage] || t.reach.en}</th>
                            <th>{t.clicks[userLanguage] || t.clicks.en}</th>
                            <th>{t.spend[userLanguage] || t.spend.en}</th>
                            <th>{t.results[userLanguage] || t.results.en}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {insightsLoading ? (
                            <tr>
                              <td colSpan={6} className="py-4 text-center">
                                <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                              </td>
                            </tr>
                          ) : rows.length === 0 ? (
                            <tr>
                              <td colSpan={6} className="py-4 text-center text-muted">
                                {t.noInsights[userLanguage] || t.noInsights.en}
                              </td>
                            </tr>
                          ) : (
                            rows.map((r, idx) => (
                              <tr key={idx}>
                                <td>{r.date_start || "-"}</td>
                                <td>{num(r.impressions).toLocaleString()}</td>
                                <td>{num(r.reach).toLocaleString()}</td>
                                <td>{num(r.clicks).toLocaleString()}</td>
                                <td>${num(r.spend).toFixed(2)}</td>
                                <td>{rowResults(r).toLocaleString()}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </CardBody>
                  </Card>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaCampaignDetail;

